import { Box, Typography, SxProps, Theme, useTheme } from '@mui/material';
import { ReactNode } from 'react';

interface QuickActionCardProps {
	icon: ReactNode;
	title: string;
	subtitle: string;
	onClick: () => void;
	backgroundColor: string;
	disabled?: boolean;
	sx?: SxProps<Theme>;
}

export const QuickActionCard = ({
	icon,
	title,
	subtitle,
	onClick,
	backgroundColor,
	disabled = false,
	sx = {}
}: QuickActionCardProps) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';
	
	// Base background: white in light mode, darker in dark mode
	const baseBackground = isDarkMode ? theme.palette.background.paper : '#ffffff';
	
	return (
		<Box
			onClick={disabled ? undefined : onClick}
			sx={{
				background: `linear-gradient(${backgroundColor}15, ${backgroundColor}15), ${baseBackground}`,
				border: `2px solid ${backgroundColor}`,
				borderRadius: 3,
				p: 2,
				cursor: disabled ? 'not-allowed' : 'pointer',
				transition: 'all 0.2s ease',
				display: 'flex',
				flexDirection: 'column',
				gap: 1.5,
				minHeight: 120,
				opacity: disabled ? 0.6 : 1,
				boxShadow: `0 2px 6px ${backgroundColor}15`,
				'&:hover': disabled ? {} : {
					transform: 'translateY(-4px)',
					boxShadow: `0 8px 24px ${backgroundColor}60`,
					background: `linear-gradient(${backgroundColor}25, ${backgroundColor}25), ${baseBackground}`
				},
				'&:active': disabled ? {} : {
					transform: 'translateY(-2px)'
				},
				...sx
			}}
		>
			{/* Icon */}
			<Box
				sx={{
					width: 40,
					height: 40,
					borderRadius: 2,
					backgroundColor: `${backgroundColor}40`,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
			>
				{icon}
			</Box>

			{/* Text */}
			<Box>
				<Typography
					variant='subtitle1'
					sx={{
						color: backgroundColor,
						fontWeight: 700,
						fontSize: '0.95rem',
						lineHeight: 1.3
					}}
				>
					{title}
				</Typography>
				<Typography
					variant='caption'
					sx={{
						color: `${backgroundColor}cc`,
						fontSize: '0.75rem',
						lineHeight: 1.4
					}}
				>
					{subtitle}
				</Typography>
			</Box>
		</Box>
	);
};
